<template>
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard")
                }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2"><span
                class="tw-text-sm">{{
                    $t("dashboard.sidebar.companies") }}</span></button>
    </div>

    <div class="tw-p-2">
        <div
            class="tw-h-full tw-w-full tw-p-4x tw-bg-whitex tw-rounded tw-borderx tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <div class="tw-flex tw-items-center tw-gap-2">
                    <FormField v-model="search" type="text" :hide-label="true" :hide-errors="true" :placeholder="$t('common.companies-search-placeholder')"
                        left-icon="solar:magnifer-linear" @input="onSearch" class="tw-bg-white tw-rounded-md tw-min-w-[300px]" />

                    <!-- Status Filter -->
                    <FormSelect v-if="false" v-model="filters.status" :options="statusOptions"
                        :placeholder="$t('common.filter-by-status', 'Filter by status')"
                        left-icon="solar:filter-bold-duotone" class="tw-bg-white tw-rounded-md tw-min-w-[150px]"  />

                </div>

                <div>
                    <a-button as="router-link" :to="{ name: 'pages.admin.companies.create' }" intent="flat" left-icon="solar:add-circle-bold-duotone"
                        class="tw-min-w-[160px]">
                        {{ $t('common.create-new-company') }}
                    </a-button>
                </div>
            </div>
            
            <!-- Table Section -->
            <CompanyTable :loading="loading" :items="companies" :options="paginationOptions" @next="onNext"
                @previous="onPrevious" @edit="onEdit" @delete="onDelete" />

            <!-- Create Company Modal -->
            <CompanyCreateModal v-if="showCreateModal" :visible="showCreateModal" @close="showCreateModal = false"
                @created="onCompanyCreated" />

            <!-- Edit Company Modal -->
            <CompanyEditModal v-if="showEditModal" :visible="showEditModal" :company="selectedCompany"
                @close="showEditModal = false" @updated="onCompanyUpdated" />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import CompanyTable from '@/components/dashboard/admin/companies/table/CompanyTable.vue';
import CompanyCreateModal from '@/components/dashboard/admin/companies/modals/CompanyCreateModal.vue';
import CompanyEditModal from '@/components/dashboard/admin/companies/modals/CompanyEditModal.vue';
import companyApi from '@/api/companies';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const companies = ref([]);
const search = ref('');
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedCompany = ref(null);

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    status: ''
});

// Status options for filter
const statusOptions = ref([
    { id: '', name: t('common.all') },
    { id: 'active', name: t('common.active') },
    { id: 'inactive', name: t('common.inactive') },
    { id: 'pending', name: t('common.pending') }
]);

// Search debounce
let searchTimeout = null;
let filterTimeout = null;

// Watch for filter changes
watch(() => filters.status, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
        fetchCompanies(1);
    }, 300);
}, { immediate: false });

// Methods
const fetchCompanies = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        const response = await companyApi.admin.paginate(params);

        if (response.data && response.data.data) {
            companies.value = response.data.data;
            paginationOptions.value = {
                current_page: response.data.current_page,
                per_page: response.data.per_page,
                total: response.data.total,
                last_page: response.data.last_page,
                next_page_url: response.data.next_page_url,
                prev_page_url: response.data.prev_page_url
            };
        }
    } catch (error) {
        console.error('Error fetching companies:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchCompanies(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchCompanies(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchCompanies(paginationOptions.value.current_page - 1);
    }
};

const onCompanyCreated = () => {
    showCreateModal.value = false;
    useAlert(t('messages.company-created', 'Company created successfully'), 'success');
    fetchCompanies(paginationOptions.value.current_page);
};

const onCompanyUpdated = () => {
    showEditModal.value = false;
    selectedCompany.value = null;
    useAlert(t('messages.company-updated', 'Company updated successfully'), 'success');
    fetchCompanies(paginationOptions.value.current_page);
};

const onEdit = (company) => {
    selectedCompany.value = company;
    showEditModal.value = true;
};

const onDelete = () => {
    // The delete functionality is handled in CompanyTableActions component
    // This is just for any additional logic if needed
    fetchCompanies(paginationOptions.value.current_page);
};

// Initialize
onMounted(() => {
    fetchCompanies();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>